import { useCallback, useEffect, useState, useRef } from "react";
import Image from 'next/image';
import { AttributeValueItem } from "@/lib/constants";
import { ProductAttributeItemProps } from "./constants";
import DatePicker from "react-datepicker";

import { escapeHtml } from '@/lib/utils';

import "react-datepicker/dist/react-datepicker.css";

// 定義狀態介面
interface AttributeState {
  readonly init: boolean;
  readonly isLoading: boolean;
  readonly currentCode: string;
  readonly attributes: {
    readonly color?: AttributeItem;
    readonly size?: AttributeItem;
    readonly [key: string]: AttributeItem | undefined;
  };
  readonly selections: {
    readonly color?: number;
    readonly size?: number;
    readonly [key: string]: number | undefined;
  };
  readonly disabled: {
    readonly color?: boolean;
    readonly size?: boolean;
    readonly [key: string]: boolean | undefined;
  };
}

type AttributeItem = {
  title: string;
  values: ReadonlyArray<AttributeValueItem>;
};

const initialState: AttributeState = {
  init: true,
  isLoading: true,
  currentCode: "",
  attributes: {},
  selections: {},
  disabled: {}
};

// 在介面定義後，新增類型映射
const attributeToSelectionMap: Record<string, keyof AttributeState['selections']> = {
  color: 'color',
  size: 'size',
  stage: 'stage',
  pattern: 'pattern',
  gender: 'gender',
  flavour: 'flavour',
  option: 'option'
};

const Attribute = (props: Readonly<ProductAttributeItemProps>) => {
  const { 
    attribute_value_list, 
    product_variant_list, 
    attributes, 
    variant, 
    setVariant,
    pregnancy,
    setDuDate,
    setClickAttribute,
    setSelectProductColor,
    use_product_option,
    product_code 
  } = props;

  const [state, setState] = useState<AttributeState>(initialState);
  const initialVariantRef = useRef<typeof variant>(null);

  // 更新屬性標題和值
  const updateAttributeData = useCallback((
    attributeType: keyof AttributeState['attributes'], 
    title: string, 
    values: ReadonlyArray<AttributeValueItem>
  ) => {
    setState(prev => {
      const newAttributes = {
        ...prev.attributes,
        [attributeType]: {
          title,
          values: values as ReadonlyArray<AttributeValueItem>
        }
      };
      return {
        ...prev,
        attributes: newAttributes
      };
    });
  }, []);

  // 更新選擇的值
  const updateSelection = useCallback((
    type: keyof AttributeState['selections'],
    value: number
  ) => {
    setState(prev => ({
      ...prev,
      selections: {
        ...prev.selections,
        [type]: value
      }
    }));
  }, []);

  // 更新禁用狀態
  const updateDisabled = useCallback((
    type: keyof AttributeState['disabled'], 
    value: boolean
  ) => {
    setState(prev => ({
      ...prev,
      disabled: {
        ...prev.disabled,
        [type]: value
      }
    }));
  }, []);

  // 產品代碼變更時的處理
  useEffect(() => {
    // 只在產品代碼變更時重置
    if (product_code && product_code !== state.currentCode) {
      // 保存初始 variant 用於屬性初始化
      initialVariantRef.current = variant;
      
      setState(prev => ({
        ...prev,
        currentCode: product_code,
        init: true,
        isLoading: true,
        disabled: {
          color: false,
          size: false,
          stage: false,
          pattern: false,
          gender: false,
          flavour: false,
          option: false
        }
      }));
    }
  }, [product_code, state.currentCode, variant]);

  // 處理變體選擇
  useEffect(() => {
    const currentSelections = [
      state.selections.color,
      state.selections.size, 
      state.selections.stage,
      state.selections.pattern,
      state.selections.gender,
      state.selections.flavour,
      state.selections.option
    ].filter((value): value is number => 
      typeof value === 'number' && value !== 0 && !isNaN(value)
    );

    const attr = [...currentSelections].sort((a, b) => a - b);
    
    // 移除根據 variant 自動補充 attribute 的邏輯，避免循環依賴
    // if (attr.length === 0 && variant) {
    //   attr.push(...variant.attribute_value_list);
    // }

    const variants = product_variant_list.filter(v => 
      [...v.attribute_value_list].sort((a, b) => a - b).join(',') === attr.join(',')
    );

    if (variants.length > 0) {
      setVariant(variants[0]);
    } else if (use_product_option) {
      const singleAttrVariants = product_variant_list.filter(v => 
        v.attribute_value_list.length === 1
      );

      if (singleAttrVariants.length > 0) {
        let variantFound = false;
        
        singleAttrVariants.forEach(singleVariant => {
          const [singleAttr] = singleVariant.attribute_value_list;
          
          if (attr.includes(singleAttr)) {
            const filtered = attr.filter(value => value !== singleAttr);
            
            filtered.forEach(attrValue => {
              Object.entries(state.selections).forEach(([key, value]) => {
                if (value === attrValue) {
                  updateSelection(key as keyof AttributeState['selections'], 0);
                  updateDisabled(key as keyof AttributeState['disabled'], true);
                }
              });
            });

            setVariant(singleVariant);
            variantFound = true;
          }
        });

        if (!variantFound) {
          setVariant(null);
        }
      }
    } else if (attr.length === 0) {
      // 只在沒有選擇任何 attribute 時才設為 null
      setVariant(null);
    }
  }, [
    state,
    product_variant_list,
    use_product_option,
    setVariant,
    updateSelection,
    updateDisabled
  ]);

  // 屬性解析
  useEffect(() => {
    const parseAttributes = async () => {
      if (!state.init || !attributes || !attribute_value_list) {
        return;
      }

      try {
        for (const attr of attributes) {
          if (!attr?.attribute_values) {
            continue;
          }

          const filtered = attr.attribute_values
            .filter(attribute => 
              attribute_value_list.includes(attribute.id)
            )
            .sort((a, b) => (a.order === b.order) ? a.id - b.id : a.order - b.order);

          if (filtered.length === 0) {
            continue;
          }

          const attributeType = attr.slug;
          if (!attributeType) {
            continue;
          }

          // 檢查 attributeType 和 對應的映射
          const normalizedType = attributeType.toLowerCase().replace(/s$/, '');

          // 使用更新的類型來更新屬性
          updateAttributeData(
            normalizedType as keyof AttributeState['attributes'],
            attr.name,
            filtered
          );

          // 使用 ref 中的初始 variant 來設定選擇，避免循環依賴
          const initialVariant = initialVariantRef.current;
          if (initialVariant?.attribute_value_list) {
            const selectedValue = filtered.find(val => 
              initialVariant.attribute_value_list.includes(val.id)
            );
            
            if (selectedValue) {
              const selectionType = attributeToSelectionMap[attributeType as keyof typeof attributeToSelectionMap];
              if (selectionType) {
                updateSelection(selectionType, selectedValue.id);
              }
            }
          }
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          init: false
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          init: false
        }));
      }
    };

    if (state.init) {
      parseAttributes();
    }
  }, [state.init, attributes, attribute_value_list, updateSelection, updateAttributeData]);

  // 懷孕狀態處理
  useEffect(() => {
    if (pregnancy !== "pregnant") {
      setDuDate(null);
    }
  }, [pregnancy, setDuDate]);

  // 處理點擊事件
  const handleClick = useCallback((type: keyof AttributeState['selections'], value: number) => {
    if (setClickAttribute) {
      setClickAttribute(true);
    }

    if (!state.disabled[type]) {
      updateSelection(type, value);
      if (type === 'color' && setSelectProductColor) {
        setSelectProductColor(value);
      }
    }
  }, [state.disabled, updateSelection, setClickAttribute, setSelectProductColor]);

  if (state.isLoading) {
    return <div>Loading...</div>;
  }

  const renderVirtualProduct = () => {
    if (!props.virtual_product) {
      return null;
    }

    return (
      <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
        {props.custom_variant_list && props.custom_variant_list.length > 0 && (
          <>
            <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">{props.custom_variant_list[0].category}</div>
            <div className="flex flex-wrap">
              {props.custom_variant_list.map((variant, index) => (
                <div
                  key={index}
                  className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${props.selectedCustomVariant !== null && variant.id === props.selectedCustomVariant.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                  onClick={() => props.setSelectedCustomVariant(variant)}
                >
                  {variant.name}
                </div>
              ))}
            </div>
          </>
        )}
        {props.selectedCustomVariant !== null && props.selectedCustomVariant.description.length > 0 && (
          <p className={`px-4 py-2 text-xs text-black border w-full border-solid rounded-lg border-attributeBorder bg-reviewBg ${props.style === "normal" ? "mt-4" : "mt-2 md:mt-4"}`} dangerouslySetInnerHTML={{__html: escapeHtml(props.selectedCustomVariant.description).replace(/(?:\r\n|\r|\n)/g, "<br />")}}></p>
        )}
        {props.style !== "normal" && (
          <>
            <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Nama</p>
            <input
              type="text"
              className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
              placeholder="Silahkan isi nama"
              value={props.toName}
              onChange={(e) => props.setToName(e.target.value)}
            />
            <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Nomor Telepon</p>
            <input
              type="text"
              className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
              placeholder="Silahkan isi nomor telepon anda"
              value={props.toMobile}
              onChange={(e) => props.setToMobile(e.target.value)}
            />
            <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Email</p>
            <input
              type="email"
              className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
              placeholder="Silahkan isi email"
              value={props.toEmail}
              onChange={(e) => props.setToEmail(e.target.value)}
            />
            {props.selectedCustomVariant !== null && (
              <>
                {props.selectedCustomVariant.pregnancy_status && (
                  <>
                    <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Kondisi Kehamilan:</p>
                    <div className="flex my-2">
                      <div
                        className="flex items-center mr-4 cursor-pointer"
                        onClick={() => props.setPregnancy("pregnant")}
                      >
                        <input
                          type="radio"
                          name="pregnancy"
                          value="pregnant"
                          className={`h-4 w-4 rounded-sm border-mooimom text-mooimom cursor-pointer focus:ring-transparent ${props.pregnancy === "pregnant" ? 'bg-mooimom border-transparent' : 'bg-white'}`}
                          checked={props.pregnancy === "pregnant"}
                          onChange={() => props.setPregnancy("pregnant")}
                        />
                        <p className="ml-2 text-xs text-black font-poppins">Hamil</p>
                      </div>
                      <div
                        className="flex items-center mr-4 cursor-pointer"
                        onClick={() => props.setPregnancy("notpregnant")}
                      >
                        <input
                          type="radio"
                          name="pregnancy"
                          value="notpregnant"
                          className={`h-4 w-4 rounded-sm border-mooimom text-mooimom cursor-pointer focus:ring-transparent ${props.pregnancy === "notpregnant" ? 'bg-mooimom border-transparent' : 'bg-white'}`}
                          checked={props.pregnancy === "notpregnant"}
                          onChange={() => props.setPregnancy("notpregnant")}
                        />
                        <p className="ml-2 text-xs text-black font-poppins">Tidak Hamil</p>
                      </div>
                      <div
                        className="flex items-center cursor-pointer"
                        onClick={() => props.setPregnancy("pregnantnotsure")}
                      >
                        <input
                          type="radio"
                          name="pregnancy"
                          value="pregnantnotsure"
                          className={`h-4 w-4 rounded-sm border-mooimom text-mooimom cursor-pointer focus:ring-transparent ${props.pregnancy === "pregnantnotsure" ? 'bg-mooimom border-transparent' : 'bg-white'}`}
                          checked={props.pregnancy === "pregnantnotsure"}
                          onChange={() => props.setPregnancy("pregnantnotsure")}
                        />
                        <p className="ml-2 text-xs text-black font-poppins">Tidak Yakin</p>
                      </div>
                    </div>
                    {props.pregnancy === "pregnant" && (
                      <>
                        <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Pada tahun dan bulan berapa anak anda akan lahir?</p>
                        <DatePicker
                          selected={props.duDate}
                          onChange={(date: Date) => props.setDuDate(date)}
                          dateFormat="yyyy-MM"
                          className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
                        />
                      </>
                    )}
                  </>
                )}
                {props.selectedCustomVariant.child_status && (
                  <>
                    <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Nama Anak</p>
                    <input
                      type="text"
                      className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
                      placeholder="Nama Anak"
                      value={props.childName}
                      onChange={(e) => props.setChildName(e.target.value)}
                    />
                    <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Umur Anak</p>
                    <input
                      type="text"
                      className="w-full px-4 py-2 mt-2 text-xs text-black border border-solid rounded-lg outline-none border-attributeBorder focus:ring-transparent focus:border-attributeBorder md:mt-1"
                      placeholder="Umur Anak (Mohon isi hanya dengan angka)"
                      value={props.childAge ? props.childAge : "" }
                      onChange={(e) => props.setChildAge(parseInt(e.target.value))}
                    />
                    <p className="mt-6 text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins md:mt-4">Jenis Kelamin Anak</p>
                    <div className="flex my-2">
                      <div
                        className="flex items-center mr-4 cursor-pointer"
                        onClick={() => props.setChildGender("boy")}
                      >
                        <input
                          type="radio"
                          name="childGender"
                          value="boy"
                          className={`h-4 w-4 rounded-sm border-mooimom text-mooimom cursor-pointer focus:ring-transparent ${props.childGender === "boy" ? 'bg-mooimom border-transparent' : 'bg-white'}`}
                          checked={props.childGender === "boy"}
                          onChange={() => props.setChildGender("boy")}
                        />
                        <p className="ml-2 text-xs text-black font-poppins">Laki-laki</p>
                      </div>
                      <div
                        className="flex items-center mr-4 cursor-pointer"
                        onClick={() => props.setChildGender("girl")}
                      >
                        <input
                          type="radio"
                          name="childGender"
                          value="girl"
                          className={`h-4 w-4 rounded-sm border-mooimom text-mooimom cursor-pointer focus:ring-transparent ${props.childGender === "girl" ? 'bg-mooimom border-transparent' : 'bg-white'}`}
                          checked={props.childGender === "girl"}
                          onChange={() => props.setChildGender("girl")}
                        />
                        <p className="ml-2 text-xs text-black font-poppins">Perempuan</p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <>
      {state.attributes['color'] && state.attributes['color'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['color'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['color'].values.map((color, index) => (
              <Image
                key={index}
                src={color.image}
                alt={color.name}
                width={props.style === "normal" ? 40 : 28}
                height={props.style === "normal" ? 40 : 28}
                quality={100}
                className={`mt-2 mr-4 cursor-pointer ${props.style === "normal" ? "rounded-none": "rounded-full"} md:rounded-none ${state.selections.color === color.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.color ? "bg-originPrice border-originPrice" : ""}`}
                onClick={() => handleClick("color", color.id)}
              />
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['size'] && state.attributes['size'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['size'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['size'].values.map((size, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.size === size.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.size ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("size", size.id)}
              >
                {size.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['stage'] && state.attributes['stage'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['stage'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['stage'].values.map((stage, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.stage === stage.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.stage ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("stage", stage.id)}
              >
                {stage.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['pattern'] && state.attributes['pattern'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['pattern'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['pattern'].values.map((pattern, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.pattern === pattern.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.pattern ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("pattern", pattern.id)}
              >
                {pattern.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['gender'] && state.attributes['gender'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['gender'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['gender'].values.map((gender, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.gender === gender.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.gender ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("gender", gender.id)}
              >
                {gender.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['flavour'] && state.attributes['flavour'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['flavour'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['flavour'].values.map((flavour, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.flavour === flavour.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.flavour ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("flavour", flavour.id)}
              >
                {flavour.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {state.attributes['option'] && state.attributes['option'].values.length > 0 && (
        <div className={`${props.style === "normal" ? "mt-8" : "mt-6 md:mt-4"}`}>
          <div className="text-sm font-semibold capitalize md:text-xs text-attributeTitle font-poppins">
            {state.attributes['option'].title}
          </div>
          <div className="flex flex-wrap">
            {state.attributes['option'].values.map((option, index) => (
              <div
                key={index}
                className={`mt-2 mr-2 py-2 px-4 border min-w-[42px] rounded-lg md:rounded text-center border-solid ${state.selections.option === option.id ? "border-mooimom border-[2px] border-solid" : "border-attributeBorder"} ${state.disabled.option ? "bg-originPrice border-originPrice" : ""} text-attributeTitle text-xs font-poppins font-medium cursor-pointer`}
                onClick={() => handleClick("option", option.id)}
              >
                {option.name}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {props.virtual_product && renderVirtualProduct()}
    </>
  )
}

export default Attribute
